import { useLocation } from "react-router-dom";
import { Mail, Phone, MapPin, MessageCircle } from "lucide-react";
import { SITE } from "@/data/site";
import { Seo } from "@/components/common/Seo";
import { PageHero } from "@/components/common/Kinetic";
import { ContactForm } from "@/components/contact/ContactForm";

const Row = ({ icon: Icon, label, value, href, testId, ext }) => (
  <a href={href} data-testid={testId} {...(ext ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    className="group flex items-start gap-4 border-b border-white/10 py-5 transition-colors last:border-0 hover:bg-white/[0.02]">
    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-white/10 text-cyan-300"><Icon className="h-4 w-4" /></span>
    <span className="min-w-0">
      <span className="eyebrow block text-slate-400">{label}</span>
      <span className="mt-1 block break-all text-[15px] font-semibold text-white group-hover:text-cyan-100">{value}</span>
    </span>
  </a>
);

const Panel = () => (
  <aside className="rounded-xl border border-white/10 bg-[#050b14] p-7 text-white sm:p-9" data-testid="contact-panel">
    <h2 className="text-xl font-bold tracking-tight">Prefer to reach us directly?</h2>
    <p className="mt-2 text-[14px] leading-relaxed text-slate-400">Email, call or message us on WhatsApp. We usually start with a short conversation about your requirements.</p>
    <div className="mt-6">
      <Row icon={Mail} label="Email" value={SITE.email} href={`mailto:${SITE.email}`} testId="mailto-direct-link" />
      <Row icon={Phone} label="Phone" value={SITE.phone} href={SITE.phoneHref} testId="phone-direct-link" />
      <Row icon={MessageCircle} label="WhatsApp" value={SITE.phone} href={SITE.whatsapp} testId="whatsapp-direct-link" ext />
      <div className="flex items-start gap-4 py-5">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-white/10 text-cyan-300"><MapPin className="h-4 w-4" /></span>
        <span><span className="eyebrow block text-slate-400">Location</span><span className="mt-1 block text-[15px] font-semibold">{SITE.location}</span><span className="mt-1 block text-[13px] text-slate-400">Working with clients in India and internationally.</span></span>
      </div>
    </div>
    <a href={SITE.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-cta"
      className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-3.5 text-[15px] font-semibold text-[#062a14] transition-[filter,transform] hover:brightness-110 active:scale-[0.98]">
      <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
    </a>
  </aside>
);

export default function Contact() {
  const { state } = useLocation();
  return (
    <>
      <Seo path="/contact" title="Contact Quantivo — Discuss Your Software Project"
        description="Discuss your SaaS, custom software, automation or payment platform project with Quantivo. Email arijit.b@quantivo.dev or WhatsApp +91 9123098973." />
      <PageHero testId="contact-hero" eyebrow="Contact" lines={["Let's talk about", "what you're building."]}
        text="Share a few details about your project and we will get back to you to understand your requirements." />
      <section className="bg-[#f8fafc] py-20 lg:py-28">
        <div className="container-q grid gap-12 lg:grid-cols-12">
          <div id="enquiry" className="scroll-mt-24 lg:col-span-7"><ContactForm initialService={state?.service ?? ""} /></div>
          <div className="lg:col-span-5"><div className="lg:sticky lg:top-28"><Panel /></div></div>
        </div>
      </section>
    </>
  );
}
